import intl from "react-intl-universal";
import { useEffect, useMemo } from "react";
import { TbCirclePlus } from "react-icons/tb";
import {
  StablesBalanceCardsSkeleton,
  StablesBalanceValueSkeleton,
} from "../../../Components/Molecules/DashboardSkeleton";
import { CurrencyIcon } from "../../../Components/Atoms/CurrencyIcon";
import { formatCurrency } from "../../../Utils/formatters";
import { useStablesBalances } from "../../../Hooks/useStablesBalances";
import { formatBrl, formatStable, getMissingStablesCreateAssets } from "./constants";
import { useStablesWalletCreate } from "./StablesWalletGate";

/** Even balance strip — avoids orphan last card (e.g. 5 cards with xl:grid-cols-4). */
export const stablesBalanceCardsGridClass =
  "grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-[repeat(auto-fit,minmax(11.5rem,1fr))]";

export const StablesBalanceCards = ({
  brlBalance = 0,
  brlBalanceFormatted,
  loadingBalance = false,
  /** When true (swap), hide fiat BRL available — only Total em BRL + owned stables. */
  stablesOnly = false,
  /** Total em BRL card — keep on home/swap; hide on buy/sell/withdraw/deposit. */
  showTotalBrl = true,
  /** Bump after a successful operation so StableBalance cards refresh. */
  refreshToken = 0,
}) => {
  const {
    assets,
    totalStablesBrl,
    loading: loadingStablesBalance,
    error: balancesError,
    reload: reloadBalances,
  } = useStablesBalances({ ownedOnly: stablesOnly });
  const { canCreateWallet, openCreate } = useStablesWalletCreate();
  const missingAssets = useMemo(
    () => getMissingStablesCreateAssets(assets),
    [assets],
  );
  const showCreateCards =
    canCreateWallet &&
    !loadingStablesBalance &&
    missingAssets.length > 0 &&
    !balancesError;

  useEffect(() => {
    if (refreshToken > 0) {
      reloadBalances();
    }
  }, [refreshToken, reloadBalances]);

  const brlDisplay = brlBalanceFormatted || formatCurrency(brlBalance || 0);

  return (
    <section className="mb-6 min-w-0">
      <h2 className="mb-3 text-sm font-bold text-brand-dark">{intl.get("stables.balanceSection")}</h2>

      {balancesError ? (
        <div className="app-card app-empty-state">
          <p>{typeof balancesError === "string" ? balancesError : intl.get("stables.balancesLoadError")}</p>
          <button type="button" className="btn-outline mt-2 !rounded-full !px-4" onClick={reloadBalances}>
            {intl.get("stables.retryLoad")}
          </button>
        </div>
      ) : loadingStablesBalance && !assets.length ? (
        <StablesBalanceCardsSkeleton />
      ) : (
        <div className={stablesBalanceCardsGridClass}>
          {showTotalBrl ? (
            <div className="summary-card min-w-0">
              <div className="flex items-center gap-2">
                <CurrencyIcon asset="brl" />
                <p className="text-xs text-gray-500">{intl.get("stables.totalBrl")}</p>
              </div>
              {loadingStablesBalance ? (
                <StablesBalanceValueSkeleton />
              ) : (
                <>
                  <p className="mt-2 text-xl font-bold text-brand-dark">
                    {formatBrl(totalStablesBrl)}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    {intl.get("stables.stablecoinsEquivalent")}
                  </p>
                </>
              )}
            </div>
          ) : null}

          {!stablesOnly ? (
            <div className="summary-card min-w-0">
              <div className="flex items-center gap-2">
                <CurrencyIcon asset="brl" />
                <p className="text-xs text-gray-500">{intl.get("stables.brlAvailableBalance")}</p>
              </div>
              {loadingBalance ? (
                <StablesBalanceValueSkeleton />
              ) : (
                <>
                  <p className="mt-2 text-xl font-bold text-brand-dark">
                    {brlDisplay}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    {intl.get("stables.sameHomeBalance")}
                  </p>
                </>
              )}
            </div>
          ) : null}

          {assets.map((asset) => (
            <div key={asset.id} className="summary-card min-w-0">
              <div className="flex items-center gap-2">
                <CurrencyIcon asset={asset.id} />
                <p className="text-xs text-gray-500">{asset.label}</p>
              </div>
              {loadingStablesBalance ? (
                <StablesBalanceValueSkeleton />
              ) : (
                <>
                  <p className="mt-2 text-xl font-bold text-brand-dark">
                    {formatStable(asset.balance, asset.id)}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    ≈ {formatBrl(asset.balance * asset.rate)}
                  </p>
                </>
              )}
            </div>
          ))}

          {showCreateCards
            ? missingAssets.map((code) => (
                <button
                  key={`create-${code}`}
                  type="button"
                  className="summary-card min-w-0 border-dashed text-left transition hover:border-[#e7a73f]/70"
                  onClick={() => openCreate(code)}
                >
                  <div className="flex items-center gap-2">
                    <CurrencyIcon asset={code} />
                    <p className="text-xs text-gray-500">{code}</p>
                  </div>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#c4892a] dark:text-[#e7a73f]">
                    <TbCirclePlus className="h-4 w-4 shrink-0" aria-hidden />
                    {intl.get("stables.noWallet.addCta", { asset: code })}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    {intl.get("stables.noWallet.addCardHint")}
                  </p>
                </button>
              ))
            : null}
        </div>
      )}
    </section>
  );
};
